import { isValidElement, type ReactNode } from 'react'
import SplitText from '#app/components/motion/split-text.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { cn } from '#app/utils/misc.tsx'

export default function ImageJumbo({
	alt,
	src,
	srcAvif,
	children,
	gradient = false,
	overlay = false,
	imageClassName,
}: {
	alt: string
	src: string
	srcAvif?: string
	overlay?: boolean
	gradient?: boolean
	children?: React.ReactNode
	imageClassName?: string
}) {
	return (
		<div className={'relative h-screen w-screen overflow-hidden'}>
			<div className={'relative z-20 h-full w-full'}>{children}</div>
			{gradient && (
				<div
					className={`absolute inset-0 z-10 bg-gradient-to-b from-transparent via-transparent via-70% to-[#04030e] to-90%`}
				/>
			)}
			{overlay && (
				<div className={'absolute inset-0 z-10 bg-[#e1e1e1] opacity-70'} />
			)}
			<picture>
				<source type={'image/avif'} srcSet={srcAvif} />
				<img
					src={src}
					alt={alt}
					className={cn(
						'absolute inset-0 z-0 h-full w-full object-cover',
						imageClassName,
					)}
				/>
			</picture>
		</div>
	)
}

export function ImageJumboContent2({
	title,
	subtitle,
	theme = 'light',
	className,
}: {
	title: string
	subtitle?: string
	theme?: 'light' | 'dark'
	className?: string
}) {
	return (
		<div
			className={cn(
				'inset-0 grid h-full w-full grid-cols-12 grid-rows-12',
				theme === 'light' ? 'text-white' : 'text-brand-dusk',
			)}
		>
			<div
				className={cn(
					'col-start-2 -col-end-2 row-start-5',
					'md:col-start-3',
					'lg:col-start-4',
					'xl:col-start-3 xl:row-start-6',
					'flex flex-col gap-sm',
					className,
				)}
			>
				{isValidElement(title) ? (
					<div>{title}</div>
				) : (
					<Text
						as={'h1'}
						size={'pageTitle'}
						type={'serif'}
						weight={'extralight'}
						className={'uppercase'}
					>
						<SplitText as={'span'}>{title}</SplitText>
					</Text>
				)}

				{subtitle && (
					<Text
						as={'p'}
						size={'p'}
						weight={'normal'}
						className={'tracking-widest'}
					>
						<SplitText as={'span'} delay={0.3}>
							{subtitle}
						</SplitText>
					</Text>
				)}
			</div>
		</div>
	)
}

export function ImageJumboContent({
	title,
	description,
	theme = 'light',
	className,
}: {
	title: string | ReactNode
	description: string
	theme?: 'light' | 'dark'
	className?: string
}) {
	return (
		<div
			className={cn(
				'inset-0 grid h-full w-full grid-cols-12 grid-rows-12',
				theme === 'light' ? 'text-white' : 'text-brand-dusk',
			)}
		>
			<div
				className={cn(
					'col-start-2 -col-end-2 row-start-4 flex flex-col gap-sm min-[450px]:col-start-3 min-[450px]:-col-end-3 md:col-start-4 lg:col-start-5 xl:col-start-7 xl:gap-lg',
					className,
				)}
			>
				{isValidElement(title) ? (
					<div>{title}</div>
				) : (
					<Text
						as={'h1'}
						size={'pageTitle'}
						type={'serif'}
						weight={'extralight'}
						className={'uppercase'}
					>
						<SplitText as={'span'}>{title}</SplitText>
					</Text>
				)}

				<Text as={'p'} size={'p'}>
					<SplitText as={'span'} delay={0.3}>
						{description}
					</SplitText>
				</Text>
			</div>
		</div>
	)
}
